/**
 * QuestionInput - Renders the appropriate input type based on question config
 */

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Question } from '@/types'
import { DollarSign, Percent, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'

interface QuestionInputProps {
  question: Question
  value: unknown
  onChange: (value: unknown) => void
  error?: string
}

export function QuestionInput({ question, value, onChange, error }: QuestionInputProps) {
  const { type, options, placeholder, validation, helpText } = question

  const inputId = `question-${question.id}`

  // Common wrapper styles
  const wrapperClass = 'space-y-2'
  const inputClass = cn(
    'text-lg h-12',
    error && 'border-destructive focus-visible:ring-destructive'
  )

  // Render based on question type
  switch (type) {
    case 'text':
      return (
        <div className={wrapperClass}>
          <Input
            id={inputId}
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={inputClass}
          />
          {helpText && <p className="text-sm text-muted-foreground">{helpText}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )

    case 'number':
    case 'year':
      return (
        <div className={wrapperClass}>
          <Input
            id={inputId}
            type="number"
            value={value !== undefined && value !== null ? String(value) : ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
            placeholder={placeholder}
            min={validation?.min}
            max={validation?.max}
            className={inputClass}
          />
          {helpText && <p className="text-sm text-muted-foreground">{helpText}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )

    case 'currency':
      return (
        <div className={wrapperClass}>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              id={inputId}
              type="number"
              value={value !== undefined && value !== null ? String(value) : ''}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
              placeholder={placeholder || '0'}
              className={cn(inputClass, 'pl-10')}
              min={0}
            />
          </div>
          {helpText && <p className="text-sm text-muted-foreground">{helpText}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )

    case 'percentage':
      return (
        <div className={wrapperClass}>
          <div className="relative">
            <Input
              id={inputId}
              type="number"
              value={value !== undefined && value !== null ? String(value) : ''}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
              placeholder={placeholder || '0'}
              className={cn(inputClass, 'pr-10')}
              min={validation?.min ?? 0}
              max={validation?.max ?? 100}
              step={0.1}
            />
            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          </div>
          {helpText && <p className="text-sm text-muted-foreground">{helpText}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )

    case 'date':
      return (
        <div className={wrapperClass}>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              id={inputId}
              type="date"
              value={(value as string) || ''}
              onChange={(e) => onChange(e.target.value)}
              className={cn(inputClass, 'pl-10')}
            />
          </div>
          {helpText && <p className="text-sm text-muted-foreground">{helpText}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )

    case 'select':
    case 'state':
    case 'industry':
      return (
        <div className={wrapperClass}>
          <Select
            value={(value as string) || ''}
            onValueChange={(val) => onChange(val)}
          >
            <SelectTrigger className={inputClass}>
              <SelectValue placeholder={placeholder || 'Select an option'} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {helpText && <p className="text-sm text-muted-foreground">{helpText}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )

    case 'radio':
      return (
        <div className={wrapperClass}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {options?.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={value === option.value ? 'default' : 'outline'}
                className={cn(
                  'h-auto py-4 px-6 justify-start text-left',
                  value === option.value && 'ring-2 ring-primary ring-offset-2'
                )}
                onClick={() => onChange(option.value)}
              >
                <span className="text-base">{option.label}</span>
              </Button>
            ))}
          </div>
          {helpText && <p className="text-sm text-muted-foreground mt-2">{helpText}</p>}
          {error && <p className="text-sm text-destructive mt-2">{error}</p>}
        </div>
      )

    case 'multi_select':
      const selectedValues = Array.isArray(value) ? value : []
      return (
        <div className={wrapperClass}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {options?.map((option) => {
              const isSelected = selectedValues.includes(option.value)
              return (
                <Button
                  key={option.value}
                  type="button"
                  variant={isSelected ? 'default' : 'outline'}
                  className={cn(
                    'h-auto py-4 px-6 justify-start text-left',
                    isSelected && 'ring-2 ring-primary ring-offset-2'
                  )}
                  onClick={() => {
                    if (isSelected) {
                      onChange(selectedValues.filter((v: string) => v !== option.value))
                    } else {
                      onChange([...selectedValues, option.value])
                    }
                  }}
                >
                  <span className="text-base">{option.label}</span>
                </Button>
              )
            })}
          </div>
          {helpText && <p className="text-sm text-muted-foreground mt-2">{helpText}</p>}
          {error && <p className="text-sm text-destructive mt-2">{error}</p>}
        </div>
      )

    case 'checkbox':
      return (
        <div className={wrapperClass}>
          <Button
            type="button"
            variant={(value as boolean) ? 'default' : 'outline'}
            className={cn(
              'h-auto py-4 px-6 justify-start text-left w-full',
              (value as boolean) && 'ring-2 ring-primary ring-offset-2'
            )}
            onClick={() => onChange(!(value as boolean))}
          >
            <span className="text-base">{placeholder || 'Yes'}</span>
          </Button>
          {helpText && <p className="text-sm text-muted-foreground mt-2">{helpText}</p>}
          {error && <p className="text-sm text-destructive mt-2">{error}</p>}
        </div>
      )

    case 'slider':
      // Basic slider implementation
      return (
        <div className={wrapperClass}>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={validation?.min ?? 0}
              max={validation?.max ?? 100}
              value={(value as number) ?? validation?.min ?? 0}
              onChange={(e) => onChange(Number(e.target.value))}
              className="flex-1 h-3 bg-muted rounded-full appearance-none cursor-pointer"
            />
            <span className="text-lg font-medium w-16 text-right">
              {value !== undefined ? String(value) : validation?.min ?? 0}
            </span>
          </div>
          {helpText && <p className="text-sm text-muted-foreground mt-2">{helpText}</p>}
          {error && <p className="text-sm text-destructive mt-2">{error}</p>}
        </div>
      )

    default:
      return (
        <div className={wrapperClass}>
          <Input
            id={inputId}
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={inputClass}
          />
          {helpText && <p className="text-sm text-muted-foreground">{helpText}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )
  }
}
